import { lazy, Suspense } from "react";
import Navigation from "@/components/Navigation";
import HeroSection from "@/components/HeroSection";
import TrustBar from "@/components/TrustBar";
import BrandsMarquee from "@/components/BrandsMarquee";
import LazyOnVisible from "@/components/LazyOnVisible";
// Everything below the first fold is lazy-loaded *and* now gated behind
// IntersectionObserver via <LazyOnVisible>. Prior state: React kicked off
// all 13 lazy chunks + their /api calls the instant the page rendered.
// Great for LCP (nothing changed) but terrible for TBT/TTI — the main
// thread was churning through code the user couldn't see. Now each
// section reserves its layout space until it's ~400px from the viewport.
const ProductRow = lazy(() => import("@/components/ProductRow"));
const BrandGrid = lazy(() => import("@/components/BrandGrid"));
const FeaturedProduct = lazy(() => import("@/components/FeaturedProduct"));
const Inno64PreordersHero = lazy(() => import("@/components/Inno64PreordersHero"));
const HeroSlider = lazy(() => import("@/components/HeroSlider"));
const DealsSection = lazy(() => import("@/components/DealsSection"));
const FlashSaleSection = lazy(() => import("@/components/FlashSaleSection"));
const WhatsAppChannelSection = lazy(() => import("@/components/WhatsAppChannelSection"));
const DropStationsSection = lazy(() => import("@/components/DropStationsSection"));
const HomeSectionDivider = lazy(() => import("@/components/HomeSectionDivider"));
const ChatBotSpotlight = lazy(() => import("@/components/ChatBotSpotlight"));
const AboutSection = lazy(() => import("@/components/AboutSection"));
const NewsletterSection = lazy(() => import("@/components/NewsletterSection"));
const InstagramStrip = lazy(() => import("@/components/InstagramStrip"));
const ChatBot = lazy(() => import("@/components/ChatBot"));
const Footer = lazy(() => import("@/components/Footer"));
import SEOHead from '@/components/SEOHead';
import HomeSeoIntro from '@/components/HomeSeoIntro';

// Tiny invisible fallback so the layout doesn't jump while the lazy
// chunk loads. Empty space is less jarring than a spinner the visitor
// wasn't looking at anyway.
const Hole = () => <div aria-hidden="true" style={{ minHeight: 'inherit' }} />;

// Rough section heights to prevent CLS while the placeholder is empty.
// Actual heights vary a bit at breakpoints — these are the "safe" values
// for the desktop layout, which is where LCP/CLS gets measured.
const H_STRIP = 220;
const H_ROW = 380;
const H_GRID = 480;
const H_HERO = 520;
const H_FOOT = 300;

export default function HomePage() {
  return (
    <>
      <SEOHead
        pageKey="home"
        path="/"
        overrides={{
          title: "OGMini — Hyderabad's Premium Diecast Experience Store | Diecast Models India",
          description: "OGMini is Hyderabad's diecast experience store. Shop 1:64 diecast models from Mini GT, INNO64, Pop Race, Tarmac Works and Hot Wheels, with delivery across India.",
          og_description: "India's Premier Diecast Collectibles Store — 500+ premium 1:64 scale models from the world's best scale-model manufacturers.",
        }}
      />
      <Navigation />
      <HeroSection />
      <TrustBar />
      <BrandsMarquee />
      <HomeSeoIntro />

      {/* Prepare the first below-fold feature just before it enters view. */}
      <LazyOnVisible minHeight={600}>
        <DropStationsSection />
      </LazyOnVisible>
      <LazyOnVisible minHeight={160}>
        <FlashSaleSection />
      </LazyOnVisible>
      <Suspense fallback={<div style={{ minHeight: 600 }} />}>
        <BrandGrid />
      </Suspense>

      {/* Deeper sections — gate behind IntersectionObserver so their
          JS + API calls only fire when scrolled near. */}
      <LazyOnVisible minHeight={H_ROW} fallback={<Hole />}>
        <ProductRow
          title="Best Sellers"
          subtitle="Most Popular"
          fetchUrl="products?best_seller=true&limit=10"
          viewAllLink="/shop?best_seller=true"
        />
      </LazyOnVisible>

      <LazyOnVisible minHeight={H_HERO} fallback={<Hole />}>
        <FeaturedProduct />
      </LazyOnVisible>

      <LazyOnVisible minHeight={H_HERO} fallback={<Hole />}>
        <HeroSlider />
      </LazyOnVisible>

      <LazyOnVisible minHeight={H_HERO} fallback={<Hole />}>
        <Inno64PreordersHero />
      </LazyOnVisible>

      <LazyOnVisible minHeight={H_GRID} fallback={<Hole />}>
        <DealsSection />
      </LazyOnVisible>

      <LazyOnVisible minHeight={H_HERO} fallback={<Hole />}>
        <ChatBotSpotlight />
      </LazyOnVisible>

      <LazyOnVisible minHeight={H_STRIP} fallback={<Hole />}>
        <HomeSectionDivider />
      </LazyOnVisible>

      <LazyOnVisible minHeight={H_STRIP} fallback={<Hole />}>
        <WhatsAppChannelSection />
      </LazyOnVisible>

      <LazyOnVisible minHeight={H_STRIP} fallback={<Hole />}>
        <AboutSection />
      </LazyOnVisible>

      {/* Instagram social-proof strip — hides itself entirely when the
          admin hasn't configured any posts, so no empty placeholder ships
          on a fresh install. */}
      <LazyOnVisible minHeight={H_STRIP} fallback={<Hole />}>
        <InstagramStrip />
      </LazyOnVisible>

      <LazyOnVisible minHeight={H_STRIP} fallback={<Hole />}>
        <NewsletterSection />
      </LazyOnVisible>

      <Suspense fallback={<div style={{ minHeight: H_FOOT }} />}>
        <Footer />
      </Suspense>

      {/* ChatBot mounts an FAB in the bottom-right corner. We keep it
          Suspense-only (no IO gate) because it's fixed-positioned and
          visitors expect it available anywhere — but the lazy import
          alone shaves ~40KB from the initial bundle. */}
      <Suspense fallback={null}>
        <ChatBot />
      </Suspense>
    </>
  );
}
